import {
    Autocomplete,
    Box,
    TextField,
    Typography,
    FormHelperText,
} from '@mui/material';
import React, { useState } from 'react';
import TreffasStyle from 'src/utils/TreffasStyle';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (value) => {
    if (!value || typeof value !== 'string') return false;
    return EMAIL_REGEX.test(value.trim());
};

const normalizeEmail = (email) => email.trim().toLowerCase();

const onAddEmail = (trimmed, currentGuests, setGuests, setEmailError) => {
    if (!trimmed) return false;
    if (!isValidEmail(trimmed)) {
        setEmailError('Please enter a valid email address.');
        return false;
    }
    const duplicate = currentGuests.some(
        (g) => normalizeEmail(g) === normalizeEmail(trimmed)
    );
    if (duplicate) {
        setEmailError('This email is already in the guest list.');
        return false;
    }
    setGuests([...currentGuests, trimmed]);
    setEmailError('');
    return true;
};


const getEmailFromOption = (v) =>
    typeof v === 'string' ? v.trim() : (v?.email || '');

const AddGuest = ({ guests = [], setGuests }) => {
    const [emailError, setEmailError] = useState('');
    const [inputValue, setInputValue] = useState('');

    const handleChange = (_, newValue) => {
        setEmailError('');
        const normalized = newValue.map(getEmailFromOption).filter(Boolean);
        const added = normalized.filter((email) => !guests.some((g) => normalizeEmail(g) === normalizeEmail(email)));
        for (const email of added) {
            const trimmed = email.trim();
            if (!isValidEmail(trimmed)) {
                setEmailError('Please enter a valid email address.');
                setGuests(guests);
                return;
            }
            const duplicate = guests.some((g) => normalizeEmail(g) === normalizeEmail(trimmed));
            if (duplicate) {
                setEmailError('This email is already in the guest list.');
                setGuests(guests);
                return;
            }
        }

        setGuests(normalized.map((e) => e.trim()));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Tab') {
            const trimmed = inputValue.trim();
            if (trimmed) {
                e.preventDefault();
                const added = onAddEmail(trimmed, guests, setGuests, setEmailError);
                if (added) setInputValue('');
            }
        }
    };

    return (
        <Box>
            <Box sx={{ mt: '24px', mb: '12px' }}>
                <Typography
                    sx={{
                        fontFamily: 'var(--font-grenettepromedium)',
                        fontSize: '18px',
                        lineHeight: '23px',
                    }}
                >
                    Guests
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 400,
                        fontSize: '14px',
                        lineHeight: '18px',
                        color: '#6F6D77',
                    }}
                >
                    Add guest emails to your appointment
                </Typography>
            </Box>

            <Box>
                <Autocomplete
                    multiple
                    freeSolo
                    value={guests}
                    inputValue={inputValue}
                    onInputChange={(_, v) => {
                        setInputValue(v);
                    }}
                    onChange={handleChange}
                    options={[]}
                    renderOption={(props, option) => {
                        if (typeof option === 'string') {
                            return <li {...props}>{option}</li>;
                        }
                        const name = [option?.firstname, option?.lastname].filter(Boolean).join(' ').trim();
                        const email = option?.email || '';
                        return (
                            <li {...props}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                    {name && <Box component="span">{name}</Box>}
                                    {email && (
                                        <Box component="span" sx={{ fontSize: '0.8em', color: 'text.secondary' }}>
                                            {email}
                                        </Box>
                                    )}
                                </Box>
                            </li>
                        );
                    }}
                    isOptionEqualToValue={(option, value) => {
                        const optEmail = getEmailFromOption(option);
                        const valEmail = typeof value === 'string' ? value : getEmailFromOption(value);
                        return normalizeEmail(optEmail) === normalizeEmail(valEmail);
                    }}
                    filterOptions={(options, state) =>
                        options.filter(
                            (opt) =>
                                typeof opt !== 'object' ||
                                !opt?.email ||
                                !guests.some((g) => normalizeEmail(g) === normalizeEmail(opt.email))
                        )
                    }
                    renderInput={(params) => (
                        <>
                            <TextField
                                {...params}
                                inputProps={{
                                    ...params.inputProps,
                                    onKeyDown: handleKeyDown,
                                }}
                                placeholder="Use tab or enter to separate email addresses"
                                error={Boolean(emailError)}
                                size="small"
                                sx={{
                                    ...TreffasStyle.formInputLarge('autocomplete'),
                                    '& .MuiOutlinedInput-root': {
                                        flexWrap: 'wrap',
                                        minHeight: 44,
                                        height: 'auto',
                                        backgroundColor: "#FFFFFF",
                                    },
                                }}
                            />
                            {emailError && (
                                <FormHelperText error>{emailError}</FormHelperText>
                            )}
                        </>
                    )}
                    sx={{
                        '& .MuiAutocomplete-input': {
                            minWidth: 180,
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

export default AddGuest;
